import React from 'react';
import { FormControl, InputLabel, Select, MenuItem, Divider } from '@mui/material';
import { AppVersionOptions } from '../hooks/useAppVersionOptions';

/**
 * "App version" list filter, shared by the driver and user lists.
 *
 * A dedicated dropdown rather than free-text, because the list search box treats a pure-digit term as
 * a phone/id lookup (backend `isPhoneSearch`) — typing "34" there would never mean a version.
 *
 * The two static buckets sit above the concrete versions and are the ones actually used day to day:
 *   Old app   — NULL appVersion; the cohort excluded from the location_ping rails.
 *   Eligible  — at or above the live gate; would receive a silent ping today.
 * Both are computed server-side against the LIVE gate, so "Eligible" keeps meaning the right thing
 * after the gate is raised. The eligible option is hidden while the gate is 0, where it would mean
 * "everyone" and be useless.
 */
interface Props {
  value: string;                 // 'all' | 'unknown' | 'eligible' | '<versionCode>'
  onChange: (v: string) => void;
  options: AppVersionOptions;
  minWidth?: number;
}

const AppVersionFilter: React.FC<Props> = ({ value, onChange, options, minWidth = 175 }) => (
  <FormControl size="small" sx={{ minWidth }}>
    <InputLabel>App version</InputLabel>
    <Select value={value} label="App version" onChange={(e) => onChange(e.target.value)}>
      <MenuItem value="all">All versions</MenuItem>
      <MenuItem value="unknown">Old app (no version)</MenuItem>
      {options.gate > 0 && (
        <MenuItem value="eligible">{`Probe-eligible (≥ ${options.gate})`}</MenuItem>
      )}
      {options.versions.length > 0 && <Divider />}
      {options.versions.map((v) => (
        <MenuItem key={v} value={String(v)}>{`Build ${v}`}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

export default AppVersionFilter;
